import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePolygonEditor, DRAFT_POLYGON_ID } from "../usePolygonEditor";
import { useSelectionAreasCrud } from "../useSelectionAreasCrud";
import { MIN_POLYGON_POINTS } from "../../utils/polygonPointMutators";
import { getSelectionAreaRgb } from "../../utils/selectionAreas";
import type {
  PolygonOverlayEntry,
  PolygonsOverlayState,
} from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/PolygonsLayer/types";
import type { PersistPolygonGeometryArgs } from "../usePolygonEditor";
import type {
  SelectionAreasState,
  UseSelectionAreasModeArgs,
  UseSelectionAreasModeResult,
} from "./types";

const ACTIVE_PAGE_INDEX = 0;

export const useSelectionAreasMode = ({
  selectedPlan,
  interactionMode,
  isSelectionAreasMode,
  areAreasVisible,
  startSelectionAreas,
  setSelectionAreasStep,
  stopSelectionAreas,
  addFloorPlanSelectionArea,
  updateFloorPlanSelectionArea,
  deleteFloorPlanSelectionArea,
}: UseSelectionAreasModeArgs): UseSelectionAreasModeResult => {
  const { t } = useTranslation();

  const step =
    interactionMode.kind === "selection-areas" ? interactionMode.step : null;
  const floorPlanId = selectedPlan?.floorPlanId;

  const areas = useMemo(
    () =>
      (selectedPlan?.selectionAreas ?? []).filter(
        (area) => area.pageIndex === ACTIVE_PAGE_INDEX,
      ),
    [selectedPlan?.selectionAreas],
  );

  const {
    isCreatingArea,
    createArea,
    updateAreaGeometry,
    toggleAreaFlag,
    setAreaMode,
    deleteArea,
  } = useSelectionAreasCrud({
    floorPlanId,
    areas,
    addFloorPlanSelectionArea,
    updateFloorPlanSelectionArea,
    deleteFloorPlanSelectionArea,
  });

  const persistGeometry = useCallback(
    ({ id, points }: PersistPolygonGeometryArgs) =>
      updateAreaGeometry({ areaId: id, points }),
    [updateAreaGeometry],
  );

  const {
    draftPoints,
    selectedTarget,
    selectedPointId,
    hoverPosition,
    modal,
    isEditingActive,
    isDraftFinalized,
    resolvePoints,
    selectTarget,
    startDrawing,
    finalizeDraft,
    requestDiscardDraft,
    requestClose,
    confirmDiscard,
    closeModal,
    requestDeletePolygon,
    onPolygonDeleted,
    onDraftSaved,
    handleMapClick,
    handleMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  } = usePolygonEditor({
    isActive: isSelectionAreasMode,
    step,
    savedPolygons: areas,
    setStep: setSelectionAreasStep,
    stop: stopSelectionAreas,
    persistGeometry,
    maxPointsMessage: t("smartSurveyor.selectionAreas.maxPointsReached"),
    minPointsMessage: t("smartSurveyor.selectionAreas.minPointsRequired"),
  });

  const openAreasPanel = useCallback(() => {
    startSelectionAreas({ pageIndex: ACTIVE_PAGE_INDEX });
  }, [startSelectionAreas]);

  const saveDraft = useCallback(async () => {
    if (!selectedPlan || !draftPoints) return;
    if (draftPoints.length < MIN_POLYGON_POINTS) return;
    const pageId = selectedPlan.pageIdByIndex.get(ACTIVE_PAGE_INDEX);
    if (pageId === undefined) return;

    const area = await createArea({
      pageId,
      pageIndex: ACTIVE_PAGE_INDEX,
      points: draftPoints,
    });
    if (!area) return;
    onDraftSaved(area.id);
  }, [selectedPlan, draftPoints, createArea, onDraftSaved]);

  const confirmDeleteArea = useCallback(async () => {
    if (modal?.type !== "deletePolygon") return;
    const { id } = modal;
    const isDeleted = await deleteArea(id);
    if (isDeleted) onPolygonDeleted(id);
    closeModal();
  }, [modal, deleteArea, onPolygonDeleted, closeModal]);

  const selectionAreasOverlay =
    useMemo<PolygonsOverlayState | null>(() => {
      const savedEntries: PolygonOverlayEntry[] = areas.map((area) => ({
        id: String(area.id),
        points: resolvePoints(area),
        rgb: getSelectionAreaRgb({ mode: area.mode }),
        isSelected:
          selectedTarget?.type === "saved" && selectedTarget.id === area.id,
        isDraft: false,
      }));

      if (!isSelectionAreasMode) {
        if (!areAreasVisible || savedEntries.length === 0) return null;
        return {
          polygons: savedEntries,
          hoverPosition: null,
          isDrawing: false,
          isInteractive: false,
          isEditable: false,
          selectedPointId: null,
        };
      }

      const entries = draftPoints
        ? [
            ...savedEntries,
            {
              id: DRAFT_POLYGON_ID,
              points: draftPoints,
              // Drafts always render in the creation color; mode only matters once saved.
              rgb: getSelectionAreaRgb({ mode: "exclude" }),
              isSelected: selectedTarget?.type === "draft",
              isDraft: true,
            },
          ]
        : savedEntries;

      return {
        polygons: entries,
        hoverPosition,
        isDrawing: step === "drawing",
        isInteractive: step === "list",
        isEditable: isEditingActive,
        selectedPointId,
        onPolygonClick: selectTarget,
        onNodeMouseDown: handleNodeMouseDown,
        onSegmentMouseDown: handleSegmentMouseDown,
      };
    }, [
      areas,
      resolvePoints,
      selectedTarget,
      isSelectionAreasMode,
      areAreasVisible,
      draftPoints,
      hoverPosition,
      step,
      isEditingActive,
      selectedPointId,
      selectTarget,
      handleNodeMouseDown,
      handleSegmentMouseDown,
    ]);

  const state: SelectionAreasState = {
    step,
    areas,
    draftPoints,
    isDraftFinalized,
    isSaving: isCreatingArea,
    selectedTarget,
    selectArea: selectTarget,
    startDrawing,
    finalizeDraft,
    saveDraft,
    requestDiscardDraft,
    toggleAreaFlag,
    setAreaMode,
    requestDeleteArea: requestDeletePolygon,
    confirmDeleteArea,
    confirmDiscard,
    modal,
    closeModal,
    requestClose,
  };

  return {
    isSelectionAreasMode,
    openAreasPanel,
    areasCount: areas.length,
    state,
    selectionAreasOverlay,
    isAreaEditingActive: isEditingActive,
    handleMapClick,
    handleMapMouseMove,
  };
};
